/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import {
  useTable,
  useExpanded,
  useRowSelect,
  useSortBy,
  Cell,
} from "react-table";
import styled from "styled-components";
import { Table, Box } from "@nordcloud/gnui";
import { ButtonChevron } from "~/components/CollapseItem/CollapseItem";
import {
  StyledTable,
  StyledRow,
  SVGIconDark,
  SVGIconLight,
  StyledTh,
  StyledTd,
  SortWrapper,
} from "./ReactTableStyles";
import { TableProps, Column, ColumnProps } from "./types";

const ExpandButtonWrapper = styled.div`
  display: flex;
  width: 24px;

  svg {
    fill: lightslategray;
  }
`;

// every react-table method generates 'key' prop
/* eslint-disable react/jsx-key */
// eslint-disable-next-line sonarjs/cognitive-complexity
function ReactTable<T extends object>({
  columns,
  data,
  defaultSort,
  getRowProps = () => ({}),
  mb,
  serverSideSort,
  expandedContent,
  noDataText = "No data available.",
}: TableProps<T>) {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const expanded = React.useMemo(() => ({ 0: data.length === 1 }), [data]);

  const allColumns = React.useMemo((): ColumnProps<T>[] => {
    if (expandedContent) {
      return [
        {
          accessor: "_expand",
          Header: "",
          disableSortBy: true,
          width: "24px",
          Cell: ({ row }) => (
            <ExpandButtonWrapper>
              <ButtonChevron
                name="chevronDown"
                isExpanded={row.isExpanded}
                isChildren
                onClick={() => row.toggleRowExpanded()}
              />
            </ExpandButtonWrapper>
          ),
        },
        ...columns,
      ];
    }

    return columns;
  }, [columns]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
  } = useTable(
    {
      columns: allColumns,
      data,
      initialState: { sortBy: defaultSort || [], expanded },
      manualSortBy: !!serverSideSort,
    },
    useSortBy,
    useExpanded,
    useRowSelect
  );

  const firstUpdate = React.useRef(true);
  React.useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
    } else if (serverSideSort) {
      serverSideSort(sortBy);
    }
  }, [sortBy, serverSideSort]);

  if (rows?.length === 0) {
    return <p>{noDataText}</p>;
  }

  return (
    <StyledTable {...getTableProps()} mb={mb}>
      <Table.thead>
        {headerGroups.map((headerGroup) => (
          <Table.tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => (
              <StyledTh
                {...column.getHeaderProps(column.getSortByToggleProps())}
                style={{
                  width: `${column.width}`,
                  cursor: column.canSort ? "pointer" : "",
                  textAlign: (column as any).center ? "center" : "left",
                }}
              >
                {column.render("Header")}
                <SortWrapper>
                  {column.canSort && column.isSorted && (
                    <SVGIconDark
                      name={column.isSortedDesc ? "sortDown" : "sortUp"}
                      size="sm"
                    />
                  )}
                  {column.canSort && !column.isSorted && (
                    <SVGIconLight name="sort" size="sm" />
                  )}
                </SortWrapper>
              </StyledTh>
            ))}
          </Table.tr>
        ))}
      </Table.thead>

      <Table.tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);

          return (
            <>
              <StyledRow {...row.getRowProps(getRowProps(row))}>
                {row.cells.map((cell: Cell) => {
                  const {
                    hasRightBorder,
                    hasLeftBorder,
                  } = (cell.column as unknown) as Column;

                  return (
                    <StyledTd
                      hasLeftBorder={hasLeftBorder}
                      hasRightBorder={hasRightBorder}
                      {...cell.getCellProps()}
                    >
                      {cell.render("Cell")}
                    </StyledTd>
                  );
                })}
              </StyledRow>

              {expandedContent && row.isExpanded && (
                <tr key={row.getRowProps({}).key + "_expand"}>
                  <td colSpan={allColumns.length}>
                    <Box border="grey" shadow="shadow01">
                      {expandedContent(row.original)}
                    </Box>
                  </td>
                </tr>
              )}
            </>
          );
        })}
      </Table.tbody>
    </StyledTable>
  );
}

export default ReactTable;
